import React, { useEffect, useState, useRef } from "react";
import Header from "../../../Componets/Header";
import { useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { get_all_invoices } from "../../../Controllers/Sales/Invoices";
import moment from "moment";
import Loadercomponent from "../../../Componets/Loader";
import ReactToPrint from "react-to-print";
import { Button, Input, Card, Statistic, Alert, Select, Tag, Collapse } from "antd";
import { PrinterOutlined, CaretRightOutlined } from "@ant-design/icons";
import { get_all_registries } from "../../../Controllers/Registry/index";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { registerCairoFont } from "../cairo-font";
import ScrollWrapper from "../../../Componets/ScrollWrapper";
import formatDateDDMMYYYY from "../../../Main/formatDate";

const { Option } = Select;
const { Panel } = Collapse;

const CashClosingReport = () => {
  const [loader, setLoader] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [registries, setRegistries] = useState([]);
  const [selectedRegistry, setSelectedRegistry] = useState("all");
  const [openingBalance, setOpeningBalance] = useState(0);
  const [expenses, setExpenses] = useState(0);
  const componentRef = useRef(null);

  const headers = {
    name: "Cash Closing Report",
    components: [
      {
        type: "range",
        name: "date",
        value: {
          start: moment().format("YYYY-MM-DD"),
          end: moment().format("YYYY-MM-DD"),
        },
      },
      {
        type: "select",
        name: "registry",
        placeholder: "Select Registry",
        options: [{ label: "All Registries", value: "all" }],
        value: "all",
      },
      {
        type: "input",
        name: "opening_balance",
        placeholder: "Opening Balance",
        value: 0,
      },
      {
        type: "input",
        name: "expenses",
        placeholder: "Expenses (Petty Cash)",
        value: 0,
      },
    ],
    navigate: {
      tabs: [
        {
          name: "Reports",
          path: "/app/reports",
        },
        {
          name: "Cash Closing",
          path: ""
        }
      ],
      total: [
        { value: 0, label: "Total" },
        { value: 0, label: "Active" },
        { value: 0, label: "Inactive" },
      ],
    },
  };

  const [header, setHeader] = useState(headers);

  const fetchRegistries = async (tableData) => {
    try {
      const data = {
        page: 1,
        limit: 1000,
        date: tableData?.date
      };

      const registriesData = await get_all_registries(data);

      if (registriesData?.status) {
        const regList = registriesData?.data?.registries || [];
        setRegistries(regList);

        let allInvoices = [];
        regList.forEach(reg => {
          const payments = reg.payments || [];

          reg.invoiceIds = (reg.invoiceIds || []).map(inv => {
            return {
              ...inv,
              payments: payments.filter(p => p.invoice === inv._id)
            };
          });

          allInvoices = [...allInvoices, ...reg.invoiceIds];
        });
        setInvoices(allInvoices);

        const updatedHeader = { ...header };
        const registryComponent = updatedHeader.components.find(c => c.name === "registry");
        if (registryComponent) {
          registryComponent.options = [
            { label: "All Registries", value: "all" },
            ...regList.map(reg => ({
              label: `Registry ${formatDateDDMMYYYY(reg.date)} - ${reg.createdBy?.name}`,
              value: reg._id
            }))
          ];
        }
        setHeader(updatedHeader);
      }
    } catch (error) {
      toast.error("Failed to fetch registries");
    }
  };

  const getFilteredInvoices = () => {
    if (selectedRegistry === "all") return invoices;

    const registry = registries.find(r => r._id === selectedRegistry);
    return registry?.invoiceIds || [];
  };

  const groupInvoicesByRegistry = () => {
    const grouped = {};

    registries.forEach(reg => {
      grouped[reg._id] = {
        registry: reg,
        invoices: reg.invoiceIds || [],
        totalSales: (reg.invoiceIds || []).reduce((sum, inv) => sum + (inv.total || 0), 0),
      };
    });

    return grouped;
  };

  const calculatePaymentSummary = (invoicesList = invoices) => {
    let cashTotal = 0;
    let cardTotal = 0;
    let onlineTotal = 0;
    let chequeTotal = 0;
    let otherTotal = 0;

    invoicesList.forEach((invoice) => {
      if (invoice.payments && invoice.payments.length > 0) {
        invoice.payments.forEach((payment) => {
          const paymentName = payment.name.toLowerCase();
          if (paymentName.includes("cash")) {
            cashTotal += payment.amount;
          } else if (
            paymentName.includes("card") ||
            paymentName.includes("credit") ||
            paymentName.includes("debit")
          ) {
            cardTotal += payment.amount;
          } else if (
            paymentName.includes("online") ||
            paymentName.includes("digital")
          ) {
            onlineTotal += payment.amount;
          } else if (
            paymentName.includes("cheque") ||
            paymentName.includes("check")
          ) {
            chequeTotal += payment.amount;
          } else {
            otherTotal += payment.amount;
          }
        });
      }
    });

    return { cashTotal, cardTotal, onlineTotal, chequeTotal, otherTotal };
  };

  const downloadCSV = () => {
    if (!invoices || invoices.length === 0) {
      toast.error("No data available to download");
      return;
    }

    try {
      // Main summary header
      const summaryHeader = ["Category", "Amount (OMR)"];
      const { cashTotal, cardTotal, onlineTotal, chequeTotal, otherTotal } = calculatePaymentSummary(filteredInvoices);
      const totalSales = filteredInvoices.reduce((sum, inv) => sum + (inv.total || 0), 0);
      const totalOpeningBalance = registries.reduce((sum, r) => sum + (r.openingBalance || 0), 0);

      const summaryRows = [
        ["Opening Balance", totalOpeningBalance.toFixed(3)],
        ["Cash Sales", cashTotal.toFixed(3)],
        ["Card Payments", cardTotal.toFixed(3)],
        ["Online Payments", onlineTotal.toFixed(3)],
        ["Cheque Receipts", chequeTotal.toFixed(3)],
        ["Other Payments", otherTotal.toFixed(3)],
        ["Total Sales", totalSales.toFixed(3)],
        ["Expenses", expenses.toFixed(3)],
        ["Net Cash", (cashTotal - expenses).toFixed(3)]
      ];

      // Invoices header and rows
      const invoicesHeader = ["Invoice #", "Customer", "Date", "Payment Methods", "Total Amount (OMR)"];
      const invoicesRows = filteredInvoices.map(invoice => {
        const paymentMethods = invoice.payments && invoice.payments.length > 0
          ? invoice.payments.map(p => `${p.name}: ${p.amount.toFixed(3)}`).join('; ')
          : "No payments";

        return [
          `#INV-${invoice.number}`,
          invoice.customer?.name || "Walk-in Customer",
          formatDateDDMMYYYY(invoice.date),
          paymentMethods,
          invoice.total.toFixed(3)
        ];
      });

      // Combine all CSV content
      let csvContent = "Cash Closing Report\n";
      csvContent += `Date: ${formatDateDDMMYYYY(header.components.find((c) => c.name === "date")?.value?.start)}\n`;
      csvContent += `Registry: ${selectedRegistry === "all" ? "All Registries" : "Selected Registry"}\n\n`;

      csvContent += "SUMMARY\n";
      csvContent += summaryHeader.join(",") + "\n";
      csvContent += summaryRows.map(row => row.join(",")).join("\n") + "\n\n";

      csvContent += "INVOICE DETAILS\n";
      csvContent += invoicesHeader.join(",") + "\n";
      csvContent += invoicesRows.map(row => row.map(field => `"${field}"`).join(",")).join("\n");

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      link.setAttribute("href", url);
      link.setAttribute("download", `Cash_Closing_Report_${moment().format('DD_MM_YYYY')}.csv`);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success("CSV downloaded successfully");

    } catch (error) {
      console.error("Error generating CSV:", error);
      toast.error("Failed to generate CSV file");
    }
  };

  const downloadPDF = () => {
    if (!invoices || invoices.length === 0) {
      toast.error("No data available to download");
      return;
    }

    try {
      const doc = new jsPDF();
      registerCairoFont(doc);
      const pageWidth = doc.internal.pageSize.getWidth();

      doc.setFont("Cairo-Bold", "bold");

      const { cashTotal, cardTotal, onlineTotal, chequeTotal, otherTotal } = calculatePaymentSummary(filteredInvoices);
      const totalSales = filteredInvoices.reduce((sum, inv) => sum + (inv.total || 0), 0);
      const totalOpeningBalance = registries.reduce((sum, r) => sum + (r.openingBalance || 0), 0);

      doc.setFontSize(18);
      doc.text("Cash Closing Report", pageWidth / 2, 18, { align: "center" });

      doc.setFontSize(10);
      doc.text(`Date: ${moment(header.components.find((c) => c.name === "date")?.value?.start).format("MMMM Do YYYY")}`, pageWidth / 2, 26, { align: "center" });
      doc.text(`Registry: ${selectedRegistry === "all" ? "All Registries" : "Selected Registry"}`, 14, 32);
      doc.text(`Generated on: ${moment().format("MMM DD, YYYY hh:mm A")}`, 14, 38);

      doc.setFontSize(12);
      doc.text("FINANCIAL SUMMARY", 14, 50);

      const summaryData = [
        ["Opening Balance", `OMR ${totalOpeningBalance.toFixed(3)}`],
        ["Cash Sales", `OMR ${cashTotal.toFixed(3)}`],
        ["Card Payments", `OMR ${cardTotal.toFixed(3)}`],
        ["Online Payments", `OMR ${onlineTotal.toFixed(3)}`],
        ["Cheque Receipts", `OMR ${chequeTotal.toFixed(3)}`],
        ["Other Payments", `OMR ${otherTotal.toFixed(3)}`],
        ["Total Sales", `OMR ${totalSales.toFixed(3)}`],
        ["Expenses", `OMR ${expenses.toFixed(3)}`],
        ["Net Cash", `OMR ${(cashTotal - expenses).toFixed(3)}`]
      ];

      autoTable(doc, {
        startY: 55,
        head: [["Category", "Amount"]],
        body: summaryData,
        theme: 'grid',
        styles: {
          font: "Cairo",
          textColor: [0, 0, 0],
          lineColor: [222, 222, 222],
          lineWidth: 0.2
        },
        headStyles: {
          font: "Cairo",
          fontStyle: "bold",
          fillColor: false,
          lineColor: [222, 222, 222],
          lineWidth: 0.2
        },
      });

      const summaryEnd = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(12);
      doc.text("INVOICE DETAILS", 14, summaryEnd);

      const invoicesData = filteredInvoices.map(invoice => {
        const paymentMethods = invoice.payments && invoice.payments.length > 0
          ? invoice.payments.map(p => `${p.name}: ${p.amount.toFixed(3)}`).join(', ')
          : "No payments";

        return [
          `#INV-${invoice.number}`,
          invoice.customer?.name || "Walk-in Customer",
          formatDateDDMMYYYY(invoice.date),
          paymentMethods,
          `OMR ${invoice.total.toFixed(3)}`
        ];
      });

      autoTable(doc, {
        startY: summaryEnd + 5,
        head: [["Invoice #", "Customer", "Date", "Payment Methods", "Total"]],
        body: invoicesData,
        theme: 'grid',
        styles: {
          font: "Cairo",
          textColor: [0, 0, 0],
          lineColor: [222, 222, 222],
          lineWidth: 0.2,
          fontSize: 8
        },
        headStyles: {
          font: "Cairo",
          fontStyle: "bold",
          fillColor: false,
          lineColor: [222, 222, 222],
          lineWidth: 0.2
        },

        columnStyles: {
          0: { cellWidth: 25 },
          1: { cellWidth: 30 },
          2: { cellWidth: 25 },
          3: { cellWidth: 50 },
          4: { cellWidth: 20 }
        }
      });

      doc.autoPrint();

      const pdfBlob = doc.output("blob");
      const pdfUrl = URL.createObjectURL(pdfBlob);

      window.open(pdfUrl);

    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF file");
    }
  };

  const formdata = (data) => {
    setOpeningBalance(parseFloat(data.opening_balance) || 0);
    setExpenses(parseFloat(data.expenses) || 0);
    setSelectedRegistry(data.registry || "all");
    fetchRegistries(data);
  };

  useEffect(() => {
    const today = {
      date: {
        start: moment().format("YYYY-MM-DD"),
        end: moment().format("YYYY-MM-DD"),
      },
      registry: "all"
    };
    fetchRegistries();
  }, []);

  const filteredInvoices = getFilteredInvoices();
  const groupedInvoices = groupInvoicesByRegistry();
  const { cashTotal, cardTotal, onlineTotal, chequeTotal, otherTotal } =
    calculatePaymentSummary(filteredInvoices);

  const renderRegistrySummary = () => {
    if (selectedRegistry !== "all") {
      const registry = registries.find(r => r._id === selectedRegistry);
      if (!registry) return null;

      return (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="text-lg font-semibold mb-2">Selected Registry Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-600">Opening Balance</p>
              <p className="font-semibold">OMR {registry.openingBalance?.toFixed(3)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Closing Balance</p>
              <p className="font-semibold">OMR {registry.closingBalance?.toFixed(3)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Sales</p>
              <p className="font-semibold">OMR {registry.totalSales?.toFixed(3)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Cashier</p>
              <p className="font-semibold">{registry.createdBy?.name}</p>
            </div>
          </div>
          <div className="mt-2">
            <Tag color={registry.isOpen ? "green" : "red"}>
              {registry.isOpen ? "OPEN" : "CLOSED"}
            </Tag>
            <span className="text-sm text-gray-600 ml-2">
              {formatDateDDMMYYYY(registry.date)}
            </span>
          </div>
        </div>
      );
    }
    return null;
  };

  const renderInvoicesTable = (invoicesList, showRegistryInfo = false) => {
    return (
      <div className="overflow-x-auto mb-6">
        <table className="w-full bg-white border-collapse">
          <thead className="text-gray-500 bg-gray-100">
            <tr>
              <th className="border p-2 text-sm font-normal">Invoice #</th>
              <th className="border p-2 text-sm font-normal">Customer</th>
              <th className="border p-2 text-sm font-normal">Date</th>
              <th className="border p-2 text-sm font-normal">Payment Methods</th>
              <th className="border p-2 text-sm font-normal">Total</th>
            </tr>
          </thead>
          <tbody>
            {invoicesList.map((invoice, index) => {
              const netAmount = (invoice.total || 0);

              return (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="border p-2 text-sm">#INV-{invoice.number}</td>
                  <td className="border p-2 text-sm">
                    {invoice.customer?.name || "Walk-in Customer"}
                  </td>
                  <td className="border p-2 text-sm">
                    {formatDateDDMMYYYY(invoice.date)}
                  </td>
                  <td className="border p-2 text-sm">
                    {invoice.payments && invoice.payments.length > 0 ? (
                      <div>
                        {invoice.payments.map((payment, i) => (
                          <div key={i} className="text-xs">
                            {payment.name}: OMR {payment.amount.toFixed(3)}
                          </div>
                        ))}
                      </div>
                    ) : (
                      "No payments"
                    )}
                  </td>
                  <td className="border p-2 text-sm font-medium">
                    OMR {invoice.total.toFixed(3)}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="bg-gray-100 font-semibold">
            <tr>
              <td colSpan="4" className="border p-2 text-right">
                Total:
              </td>
              <td className="border p-2">
                OMR{" "}
                {invoicesList
                  .reduce((sum, invoice) => sum + (invoice.total || 0), 0)
                  .toFixed(3)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    );
  };

  return (
    <Header headers={header} setHeaders={setHeader} formdata={formdata}>
      <Loadercomponent loader={loader}>

        <div className="text-right flex gap-3 justify-end mt-4">
          <Button
            className="mr-2  !bg-[#07b553] !border-[#07b553] hover:!bg-[#38cb82] hover:!border-[#38cb82]"
            type="primary"
            onClick={downloadCSV}
          >
            Download CSV
          </Button>

          <Button
            className="mr-2  !bg-[#07b553] !border-[#07b553] hover:!bg-[#38cb82] hover:!border-[#38cb82]"
            type="primary"
            onClick={downloadPDF}
          >
            Download PDF
          </Button>
        </div>

        <ScrollWrapper>

          <div ref={componentRef}>
            <div className="mb-6">
              <h2 className="text-xl font-bold text-center">
                Cash Closing Report
              </h2>
              {/* <p className="text-center text-gray-600">
                {formatDateDDMMYYYY(
                  header.components.find((c) => c.name === "date")?.value?.start
                ).format("MMMM Do YYYY")}
                {selectedRegistry !== "all" && " - Selected Registry"}
              </p>
{/*  */}
              <p className="text-center text-gray-600">
                {moment(header.components.find((c) => c.name === "date")?.value?.start).format("MMMM Do YYYY")}
                {selectedRegistry !== "all" && " - Selected Registry"}
              </p>

            </div>

            {renderRegistrySummary()}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-4 mb-6 print-grid-3">
              <Card>
                <h4 className="font-semibold mb-2">Opening Balance</h4>
                {registries.length > 0 ? (
                  <>
                    <ul className="text-sm mb-2">
                      {registries.map((reg, i) => (
                        <li key={i} className="flex justify-between border-b py-1">
                          <span>
                            Registry {formatDateDDMMYYYY(reg.date)} - {reg.createdBy?.name}
                          </span>
                          <span>OMR {reg.openingBalance?.toFixed(3)}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="font-semibold text-right pt-1 border-t">
                      Total: OMR{" "}
                      {registries
                        .reduce((sum, r) => sum + (r.openingBalance || 0), 0)
                        .toFixed(3)}
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-gray-500">No registries found</p>
                )}
              </Card>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6 print-grid-4">
              <Card>
                <Statistic
                  title="Cash Sales"
                  value={cashTotal}
                  precision={3}
                  prefix="OMR"
                />
              </Card>
              <Card>
                <Statistic
                  title="Card Payments"
                  value={cardTotal}
                  precision={3}
                  prefix="OMR"
                />
              </Card>
              <Card>
                <Statistic
                  title="Online Payments"
                  value={onlineTotal}
                  precision={3}
                  prefix="OMR"
                />
              </Card>
              <Card>
                <Statistic
                  title="Cheque Receipts"
                  value={chequeTotal}
                  precision={3}
                  prefix="OMR"
                />
              </Card>
              <Card>
                <Statistic
                  title="Other Payments"
                  value={otherTotal}
                  precision={3}
                  prefix="OMR"
                />
              </Card>
            </div>

            {selectedRegistry === "all" ? (
              <Collapse
                defaultActiveKey={Object.keys(groupedInvoices).filter(key => groupedInvoices[key].invoices.length > 0)}
                expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                className="mb-6"
              >
                {Object.entries(groupedInvoices).map(([registryId, group]) => {
                  if (group.invoices.length === 0) return null;

                  return (
                    <Panel
                      header={
                        <div className="flex justify-between items-center">
                          <span>
                            {registryId === "unassigned"
                              ? "Unassigned Invoices"
                              : `Registry ${formatDateDDMMYYYY(group.registry.date)} - ${group.registry.createdBy?.name}`}
                          </span>
                          <div className="flex items-center gap-4">
                            <Tag color={registryId === "unassigned" ? "orange" : group.registry.isOpen ? "green" : "red"}>
                              {registryId === "unassigned" ? "UNASSIGNED" : group.registry.isOpen ? "OPEN" : "CLOSED"}
                            </Tag>
                            <span className="font-semibold">
                              OMR {group.totalSales.toFixed(3)} • {group.invoices.length} invoices
                            </span>
                          </div>
                        </div>
                      }
                      key={registryId}
                    >
                      {renderInvoicesTable(group.invoices, true)}
                    </Panel>
                  );
                })}
              </Collapse>
            ) : (
              renderInvoicesTable(filteredInvoices)
            )}
          </div>
        </ScrollWrapper>
      </Loadercomponent>
    </Header>
  );
};

export default CashClosingReport;